/**
 * @ngdoc service
 * @name fileUtils
 * @description
 * The file utilities service.
 *
 * @memberof common
 */
class FileUtils {
  /**
   * Class constructor.
   * @param {Service} $q                     To reject promises.
   * @param {Object}  appConstant            To get the allowed max file size.
   * @param {Service} contentCreatorAdminAPI To upload files to the BE.
   * @param {Service} fileTypes              To be able check the files type.
   * @ignore
   */
  constructor(
    $q,
    appConstant,
    contentCreatorAdminAPI,
    fileTypes,
  ) {
    'ngInject';

    /**
     * The local reference to the `$q` service.
     * @type {Object}
     */
    this.$q = $q;
    /**
     * The local reference to the `contentCreatorAdminAPI` service.
     * @type {Service}
     */
    this.contentCreatorAdminAPI = contentCreatorAdminAPI;
    /**
     * The local reference to the `fileTypes` service.
     * @type {Service}
     */
    this.fileTypes = fileTypes;
    /**
     * The limit of the filesize per upload.
     * @type {Number}
     */
    this.maxFileSize = appConstant.maxFileSize;
    /**
     * The defaultSize to return in formatBytes.
     * @type {String}
     */
    this.defaultSize = '0 Bytes';
    /**
     * The posible size types array.
     * @type {Array}
     */
    this.sizeTypes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    /**
     * The human readable limit of the filesize per upload.
     * @type {Number}
     */
    this.displayMaxFileSize = this._formatBytes(this.maxFileSize);
    /**
     * The local reference to the `errors` object.
     * @type {Object}
     */
    this.errors = appConstant.errors.uploadImage;

    // Set the maxFileSize in the error string
    this.errors.invalidSize = this.errors.invalidSize.replace(
      '{{maxFileSize}}',
      this.displayMaxFileSize,
    );
  }
  _formatBytes(bytes) {
    if (bytes === 0) {
      return this.defaultSize;
    }
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const val = parseFloat((bytes / (k ** i)).toFixed(0));

    return `${val}${this.sizeTypes[i]}`;
  }
  /**
   * Evaluate if a File Object is an valid Image.
   * @param  {Object} file file object.
   * @return {Boolean}
   */
  isValidImage(file) {
    return (file.type && this.fileTypes.isAnImageType(file.type));
  }
  /**
   * Evaluate if a File Object has a valid size.
   * @param  {Object} file file object.
   * @return {Boolean}
   */
  hasRightSize(file) {
    return (file.size < this.maxFileSize);
  }
  /**
   * Validate the file type is an image and its size, and upload it to the BE.
   *
   * @param {File}   file         The image file to upload.
   * @param {String} resourceType The type of resource we are uploading.
   * @return {Promise}
   */
  uploadImage(file, resourceType) {
    const isValidImage = this.isValidImage(file);
    const rightSize = this.hasRightSize(file);

    return (isValidImage && rightSize)
      ? this.contentCreatorAdminAPI.uploadImage(
        file,
        resourceType,
      ).catch(() => this.$q.reject(new Error(this.errors.fail)))
      : this.$q.reject(new Error((isValidImage
        ? this.errors.invalidSize
        : this.errors.invalidImage
      )));
  }
}

export default FileUtils;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/services/fileUtils.js